import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    Avatar,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel,
    Tooltip,
    Alert,
    Checkbox,
    CircularProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    Refresh as RefreshIcon,
    TrendingUp as TrendingUpIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Pause as PauseIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    PlayArrow as PlayArrowIcon,
    Stop as StopIcon,
    Block as BlockIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import StrategyDetailsModal from './StrategyDetailsModal';
import CreateStrategyWizard from './CreateStrategyWizard';
import EditStrategyModal from './EditStrategyModal'; // B2: Edit modal
// import PerformanceChart from './PerformanceChart'; // Removed - charts hidden
import { toast } from 'react-toastify';
import {
    getAllStrategies,
    getStrategyStats,
    forceStopStrategy,
    bulkControlStrategies,
    toggleStrategyStatus,
    getAllUsers
} from '../../api/admin_api';

const AdminStrategyManagement = forwardRef((props, ref) => {
    const navigate = useNavigate();

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        openCreateStrategyModal: () => {
            setCreateWizardOpen(true);
        }
    }), []);

    // Helper function to generate initials
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // State management
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [createWizardOpen, setCreateWizardOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false); // B2: Edit modal state
    const [selectedStrategyForEdit, setSelectedStrategyForEdit] = useState(null); // B2: Strategy to edit
    const [selectedStrategyForDetails, setSelectedStrategyForDetails] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('all');
    const [accountFilter, setAccountFilter] = useState('');
    const [dashboardFilter, setDashboardFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedStrategy, setSelectedStrategy] = useState(null);

    // Data states
    const [strategies, setStrategies] = useState([]);
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState({
        totalStrategies: 0,
        activeStrategies: 0,
        stoppedStrategies: 0,
        errorStrategies: 0,
        totalUsers: 0
    });

    // Force Stop Dialog
    const [forceStopDialogOpen, setForceStopDialogOpen] = useState(false);
    const [strategyToStop, setStrategyToStop] = useState(null);
    const [stopReason, setStopReason] = useState('');

    // Bulk Action Dialog
    const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
    const [selectedStrategyIds, setSelectedStrategyIds] = useState([]);
    const [bulkAction, setBulkAction] = useState('stop');

    // C3: Stop & Disable confirmation dialog for running strategies
    const [stopDisableDialogOpen, setStopDisableDialogOpen] = useState(false);
    const [strategyToDisable, setStrategyToDisable] = useState(null);

    const rowsPerPage = 5;

    // Fetch data on component mount and filter changes
    useEffect(() => {
        fetchAllData();
    }, [statusFilter, userFilter, accountFilter]);

    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchAllData(true);
        }, 30000);

        return () => clearInterval(interval);
    }, [statusFilter, userFilter, accountFilter]);

    const fetchAllData = async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);

        try {
            // Fetch strategies, stats, and users in parallel
            const [strategiesRes, statsRes, usersRes] = await Promise.all([
                getAllStrategies({
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    userId: userFilter !== 'all' ? userFilter : undefined,
                    accountNumber: accountFilter || undefined
                }),
                getStrategyStats(),
                getAllUsers()
            ]);

            // Handle different response formats
            const strategiesData = Array.isArray(strategiesRes)
                ? strategiesRes
                : (strategiesRes?.data || strategiesRes || []);

            // Map backend DTO to frontend format
            const mappedStrategies = strategiesData.map(strategy => ({
                // Map backend field names to frontend field names
                id: strategy.strategyId,
                name: strategy.strategyName,
                description: strategy.description || '',
                userName: strategy.ownerName,
                userEmail: strategy.ownerEmail,
                userId: strategy.ownerUserId,
                accountNumber: strategy.accountNumber,
                strategyType: strategy.strategyType,
                status: strategy.status,
                isEnabled: strategy.isEnabled,
                createdAt: strategy.createdAt,
                lastModifiedAt: strategy.lastModifiedAt,

                // Parse symbols from backend format
                futuresSymbol: strategy.futuresSymbol || strategy.symbol || 'N/A',
                spotSymbol: strategy.spotSymbol || strategy.symbol || 'N/A',

                // Create symbols array for backward compatibility
                symbols: [
                    strategy.futuresSymbol || strategy.symbol || 'N/A',
                    strategy.spotSymbol || strategy.symbol || 'N/A'
                ],

                // Default values for missing fields
                totalTrades: strategy.totalTrades || 0,
                totalPnL: strategy.totalPnL || 0,
                errorMessage: strategy.errorMessage || null,

                // Dashboard routing and entry mode (defensive extraction)
                dashboardType: strategy.dashboardType || strategy.DashboardType || 'Algo.01',
                entryMode: strategy.entryMode || strategy.parameters?.EntryMode || strategy.parameters?.entryMode || 'Benchmark'
            }));

            const statsData = statsRes?.data || statsRes || {
                totalStrategies: 0,
                activeStrategies: 0,
                stoppedStrategies: 0,
                errorStrategies: 0,
                totalUsers: 0
            };

            const usersData = usersRes?.data || usersRes || [];

            setStrategies(mappedStrategies);
            setStats(statsData);
            setUsers(usersData);

            console.log('Fetched strategies:', strategiesData.length);
            console.log('Fetched stats:', statsData);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load strategies');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        fetchAllData();
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
        setPage(1);
    };

    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(1);
    };

    const handleUserFilterChange = (event) => {
        setUserFilter(event.target.value);
        setPage(1);
    };

    const handleAccountFilterChange = (event) => {
        setAccountFilter(event.target.value);
        setPage(1);
    };

    const handleDashboardFilterChange = (event) => {
        setDashboardFilter(event.target.value);
        setPage(1);
    };

    const handleMenuOpen = (event, strategy) => {
        setAnchorEl(event.currentTarget);
        setSelectedStrategy(strategy);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedStrategy(null);
    };

    const handleViewDetails = (strategy = null) => {
        setSelectedStrategyForDetails(strategy || selectedStrategy);
        setDetailsModalOpen(true);
        if (!strategy) {
            handleMenuClose();
        }
    };

    // B2: Handle edit strategy
    const handleEditStrategy = () => {
        setSelectedStrategyForEdit(selectedStrategy);
        setEditModalOpen(true);
        handleMenuClose();
    };

    // B2: Handle edit save
    const handleEditSave = (updatedData) => {
        setEditModalOpen(false);
        setSelectedStrategyForEdit(null);
        fetchAllData(); // Refresh list
    };

    const handleForceStop = () => {
        setStrategyToStop(selectedStrategy);
        setForceStopDialogOpen(true);
        handleMenuClose();
    };

    const confirmForceStop = async () => {
        if (!strategyToStop) return;

        try {
            const result = await forceStopStrategy(strategyToStop.id);

            if (result.success) {
                toast.success(`Strategy "${strategyToStop.name}" force stopped successfully`);
                setForceStopDialogOpen(false);
                setStopReason('');
                setStrategyToStop(null);
                fetchAllData();
            } else {
                toast.error(result.error || 'Failed to force stop strategy');
            }
        } catch (error) {
            console.error('Error force stopping strategy:', error);
            toast.error('Failed to force stop strategy');
        }
    };

    // C1/C3: Handle enable/disable with safety check for running strategies
    const handleToggleEnabled = async (strategy = null) => {
        const targetStrategy = strategy || selectedStrategy;
        if (!targetStrategy) return;

        const isCurrentlyEnabled = targetStrategy.isEnabled !== false;
        const isRunning = targetStrategy.status === 'running' || targetStrategy.status === 'Running';

        // C3: If trying to DISABLE a RUNNING strategy, show confirmation
        if (isCurrentlyEnabled && isRunning) {
            setStrategyToDisable(targetStrategy);
            setStopDisableDialogOpen(true);
            handleMenuClose();
            return;
        }

        // Otherwise, proceed with toggle
        try {
            const newStatus = !isCurrentlyEnabled;
            const result = await toggleStrategyStatus(targetStrategy.id, newStatus);

            if (result.success) {
                toast.success(`Strategy ${newStatus ? 'enabled' : 'disabled'} successfully`);
                fetchAllData(true);
            } else {
                toast.error(result.error || 'Failed to toggle strategy status');
            }
        } catch (error) {
            console.error('Error toggling strategy status:', error);
            toast.error('Failed to toggle strategy status');
        }
        handleMenuClose();
    };

    // C3: Confirm Stop & Disable for running strategy
    const confirmStopAndDisable = async () => {
        if (!strategyToDisable) return;

        try {
            // First, stop the strategy
            const stopResult = await forceStopStrategy(strategyToDisable.id);

            if (stopResult.success) {
                // Then disable it
                const disableResult = await toggleStrategyStatus(strategyToDisable.id, false);

                if (disableResult.success) {
                    toast.success(`Strategy "${strategyToDisable.name}" stopped and disabled successfully`);
                    fetchAllData();
                } else {
                    toast.error(disableResult.error || 'Strategy stopped but failed to disable');
                }
            } else {
                toast.error(stopResult.error || 'Failed to stop strategy');
            }
        } catch (error) {
            console.error('Error stopping and disabling strategy:', error);
            toast.error('Failed to stop and disable strategy');
        } finally {
            setStopDisableDialogOpen(false);
            setStrategyToDisable(null);
        }
    };

    const handleBulkAction = () => {
        if (selectedStrategyIds.length === 0) {
            toast.warning('Please select at least one strategy');
            return;
        }
        setBulkActionDialogOpen(true);
    };

    const confirmBulkAction = async () => {
        if (selectedStrategyIds.length === 0) return;

        try {
            const result = await bulkControlStrategies(selectedStrategyIds, bulkAction);

            if (result.success) {
                toast.success(`Bulk ${bulkAction} completed successfully`);
                setBulkActionDialogOpen(false);
                setSelectedStrategyIds([]);
                fetchAllData();
            } else {
                toast.error(result.error || 'Failed to perform bulk action');
            }
        } catch (error) {
            console.error('Error in bulk action:', error);
            toast.error('Failed to perform bulk action');
        }
    };

    const handleSelectStrategy = (strategyId) => {
        setSelectedStrategyIds(prev => {
            if (prev.includes(strategyId)) {
                return prev.filter(id => id !== strategyId);
            } else {
                return [...prev, strategyId];
            }
        });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allIds = filteredStrategies.map(s => s.id);
            setSelectedStrategyIds(allIds);
        } else {
            setSelectedStrategyIds([]);
        }
    };

    // Filter strategies
    const filteredStrategies = strategies.filter(strategy => {
        const matchesSearch = strategy.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            strategy.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            strategy.userEmail?.toLowerCase().includes(searchQuery.toLowerCase());

        // Dashboard filter logic (supports both dot and hyphen formats for backward compatibility)
        const strategyDashboard = strategy.dashboardType || strategy.DashboardType || 'Algo.01';
        const matchesDashboard = dashboardFilter === 'all' ||
                                strategyDashboard === dashboardFilter ||
                                strategyDashboard === dashboardFilter.replace('.', '-'); // Backward compatibility: Algo.02 matches Algo-2

        return matchesSearch && matchesDashboard;
    });

    // Pagination
    const paginatedStrategies = filteredStrategies.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
            case 'running':
                return 'success';
            case 'stopped':
            case 'paused':
                return 'default';
            case 'error':
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
            case 'running':
                return <CheckCircleIcon fontSize="small" />;
            case 'stopped':
            case 'paused':
                return <PauseIcon fontSize="small" />;
            case 'error':
            case 'failed':
                return <ErrorIcon fontSize="small" />;
            default:
                return <BlockIcon fontSize="small" />;
        }
    };

    return (
        <Box sx={{ padding: 3, background: 'rgba(51, 53, 57, 1)', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '2.5rem' }}>🧠</Typography>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                            Strategy Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', marginTop: 0.5 }}>
                            Monitor and manage user strategies
                        </Typography>
                    </Box>
                    <Tooltip title="Refresh Data">
                        <IconButton
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                            sx={{
                                background: 'rgba(255, 255, 255, 0.1)',
                                color: '#ffffff',
                                '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
                            }}
                        >
                            {refreshing ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : <RefreshIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ marginBottom: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        background: 'rgba(41, 43, 47, 1)',
                        borderTop: '4px solid #7c3aed',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        height: '100%'
                    }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                                Total Strategies
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, marginTop: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
                                {stats.totalStrategies}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#22c55e', fontSize: '0.875rem', marginTop: 0.5 }}>
                                +8 this week
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        background: 'rgba(41, 43, 47, 1)',
                        borderTop: '4px solid #22c55e',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        height: '100%'
                    }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                                Active Running
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, marginTop: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
                                {stats.activeStrategies}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#22c55e', fontSize: '0.875rem', marginTop: 0.5 }}>
                                26.2% of total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        background: 'rgba(41, 43, 47, 1)',
                        borderTop: '4px solid #06b6d4',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        height: '100%'
                    }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                                Total P&L
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, marginTop: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
                                ${(stats.totalPnL || 45230).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#22c55e', fontSize: '0.875rem', marginTop: 0.5 }}>
                                +12.3%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        background: 'rgba(41, 43, 47, 1)',
                        borderTop: '4px solid #f59e0b',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        height: '100%'
                    }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                                Win Rate
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, marginTop: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
                                {stats.winRate || '72.5%'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#22c55e', fontSize: '0.875rem', marginTop: 0.5 }}>
                                +2.1%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginBottom: 3 }}>
                <Button
                    variant="contained"
                    onClick={() => navigate('/adminPanel/templates')}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 16px rgba(124, 58, 237, 0.4)'
                        },
                        transition: 'all 0.2s'
                    }}
                >
                    Prebuilt-Strategies
                </Button>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateWizardOpen(true)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 16px rgba(124, 58, 237, 0.4)'
                        },
                        transition: 'all 0.2s'
                    }}
                >
                    Create Strategy
                </Button>
            </Box>

            {/* Recent Strategies Section */}
            <Box sx={{ marginBottom: 3 }}>
                {/* Section Header */}
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)', marginBottom: 3 }}>
                    Recent Strategies
                </Typography>

                {/* Inline Filters */}
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
                    {/* Search Bar */}
                    <TextField
                        placeholder="Search strategies..."
                        value={searchQuery}
                        onChange={handleSearch}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                                </InputAdornment>
                            ),
                            sx: { color: '#ffffff' }
                        }}
                        sx={{
                            background: 'rgba(41, 43, 47, 1)',
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                            }
                        }}
                    />

                    {/* Status Filter */}
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={handleStatusFilterChange}
                            displayEmpty
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        background: 'rgba(41, 43, 47, 1)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        '& .MuiMenuItem-root': {
                                            color: '#ffffff',
                                            '&:hover': {
                                                background: 'rgba(124, 58, 237, 0.2)'
                                            },
                                            '&.Mui-selected': {
                                                background: 'rgba(124, 58, 237, 0.3)',
                                                '&:hover': {
                                                    background: 'rgba(124, 58, 237, 0.4)'
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                            sx={{
                                background: 'rgba(41, 43, 47, 1)',
                                color: '#ffffff',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' },
                                '.MuiSvgIcon-root': { color: '#ffffff' }
                            }}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="active">Running</MenuItem>
                            <MenuItem value="stopped">Stopped</MenuItem>
                            <MenuItem value="error">Error</MenuItem>
                        </Select>
                    </FormControl>

                    {/* User Filter */}
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>User</InputLabel>
                        <Select
                            value={userFilter}
                            label="User"
                            onChange={handleUserFilterChange}
                            displayEmpty
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        background: 'rgba(41, 43, 47, 1)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        '& .MuiMenuItem-root': {
                                            color: '#ffffff',
                                            '&:hover': {
                                                background: 'rgba(124, 58, 237, 0.2)'
                                            },
                                            '&.Mui-selected': {
                                                background: 'rgba(124, 58, 237, 0.3)',
                                                '&:hover': {
                                                    background: 'rgba(124, 58, 237, 0.4)'
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                            sx={{
                                background: 'rgba(41, 44, 49, 1)',
                                color: '#ffffff',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' },
                                '.MuiSvgIcon-root': { color: '#ffffff' }
                            }}
                        >
                            <MenuItem value="all" sx={{ color: '#ffffff' }}>All Users</MenuItem>
                            {users.map((user) => (
                                <MenuItem key={user.id} value={user.id} sx={{ color: '#ffffff' }}>
                                    {user.firstName || user.name} {user.lastName || ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Dashboard Filter */}
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Dashboard</InputLabel>
                        <Select
                            value={dashboardFilter}
                            label="Dashboard"
                            onChange={handleDashboardFilterChange}
                            displayEmpty
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        background: 'rgba(41, 43, 47, 1)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        '& .MuiMenuItem-root': {
                                            color: '#ffffff',
                                            '&:hover': {
                                                background: 'rgba(124, 58, 237, 0.2)'
                                            },
                                            '&.Mui-selected': {
                                                background: 'rgba(124, 58, 237, 0.3)',
                                                '&:hover': {
                                                    background: 'rgba(124, 58, 237, 0.4)'
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                            sx={{
                                background: 'rgba(41, 43, 47, 1)',
                                color: '#ffffff',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' },
                                '.MuiSvgIcon-root': { color: '#ffffff' }
                            }}
                        >
                            <MenuItem value="all">All Dashboards</MenuItem>
                            <MenuItem value="Algo.01">Algo.01 - Legacy</MenuItem>
                            <MenuItem value="Algo.02">Algo.02 - Analytics</MenuItem>
                            <MenuItem value="Algo.03">Algo.03 - Reserved</MenuItem>
                            <MenuItem value="Algo.04">Algo.04 - Reserved</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {loading && <LinearProgress sx={{ background: 'rgba(255, 255, 255, 0.1)', '& .MuiLinearProgress-bar': { background: '#7c3aed' }, marginBottom: 2 }} />}

                {/* Strategy Cards */}
                {paginatedStrategies.length === 0 ? (
                    <Box sx={{ textAlign: 'center', padding: 8, background: 'rgba(41, 43, 47, 1)', borderRadius: '12px' }}>
                        <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            {loading ? 'Loading strategies...' : 'No strategies found'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {paginatedStrategies.map((strategy, index) => {
                            // Avatar colors cycling through gradient options
                            const avatarColors = [
                                'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',  // Purple
                                'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',  // Cyan
                                'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'   // Orange
                            ];
                            const avatarColor = avatarColors[index % avatarColors.length];

                            return (
                                <Card
                                    key={strategy.id}
                                    sx={{
                                        marginBottom: 1,
                                        background: 'rgba(41, 43, 47, 1)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '8px',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                            transform: 'translateY(-2px)',
                                            borderColor: 'rgba(255, 255, 255, 0.2)'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ padding: '12px 16px', '&:last-child': { paddingBottom: '12px' } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {/* Avatar */}
                                            <Avatar
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    background: avatarColor,
                                                    fontWeight: 700,
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {getInitials(strategy.name)}
                                            </Avatar>

                                            {/* Strategy Info */}
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff', marginBottom: 0.25, fontSize: '0.9375rem' }}>
                                                    {strategy.name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8125rem' }}>
                                                        {strategy.userName || 'Unknown'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8125rem' }}>•</Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8125rem' }}>
                                                        {strategy.futuresSymbol && strategy.spotSymbol
                                                            ? `${strategy.futuresSymbol}-${strategy.spotSymbol}`
                                                            : 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8125rem' }}>•</Typography>
                                                    <Chip
                                                        label={strategy.strategyType || 'V2'}
                                                        size="small"
                                                        sx={{
                                                            height: 18,
                                                            background: '#7c3aed',
                                                            color: '#ffffff',
                                                            fontSize: '0.6875rem',
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': { padding: '0 6px' }
                                                        }}
                                                    />
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8125rem' }}>•</Typography>
                                                    {/* Dashboard Type Badge */}
                                                    <Chip
                                                        label={strategy.dashboardType || 'Algo.01'}
                                                        size="small"
                                                        sx={{
                                                            height: 18,
                                                            background: strategy.dashboardType === 'Algo.02' || strategy.dashboardType === 'Algo-2' ? '#06b6d4' :
                                                                       strategy.dashboardType === 'Algo.03' || strategy.dashboardType === 'Algo-3' ? '#f59e0b' :
                                                                       strategy.dashboardType === 'Algo.04' || strategy.dashboardType === 'Algo-4' ? '#22c55e' : '#7c3aed',
                                                            color: '#ffffff',
                                                            fontSize: '0.6875rem',
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': { padding: '0 6px' }
                                                        }}
                                                    />
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8125rem' }}>•</Typography>
                                                    {/* Entry Mode Badge */}
                                                    <Chip
                                                        label={strategy.entryMode || 'Benchmark'}
                                                        size="small"
                                                        sx={{
                                                            height: 18,
                                                            background: strategy.entryMode === 'Percentile' ? '#3b82f6' :
                                                                       strategy.entryMode === 'BetaSpread' ? '#8b5cf6' :
                                                                       strategy.entryMode === 'Z-Score' ? '#10b981' : '#6b7280',
                                                            color: '#ffffff',
                                                            fontSize: '0.6875rem',
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': { padding: '0 6px' }
                                                        }}
                                                    />
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8125rem' }}>•</Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8125rem' }}>
                                                        2h ago
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Status Badge */}
                                            <Chip
                                                label={strategy.status || 'Unknown'}
                                                size="small"
                                                sx={{
                                                    height: 24,
                                                    background: strategy.status === 'active' || strategy.status === 'running'
                                                        ? 'rgba(34, 197, 94, 0.2)'
                                                        : strategy.status === 'stopped'
                                                            ? 'rgba(239, 68, 68, 0.2)'
                                                            : 'rgba(251, 146, 60, 0.2)',
                                                    color: strategy.status === 'active' || strategy.status === 'running'
                                                        ? '#22c55e'
                                                        : strategy.status === 'stopped'
                                                            ? '#ef4444'
                                                            : '#fb923c',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    border: `1px solid ${strategy.status === 'active' || strategy.status === 'running'
                                                        ? '#22c55e'
                                                        : strategy.status === 'stopped'
                                                            ? '#ef4444'
                                                            : '#fb923c'
                                                        }`,
                                                    '& .MuiChip-label': { padding: '0 8px' }
                                                }}
                                            />

                                            {/* P&L */}
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: (strategy.totalPnL || 0) >= 0 ? '#22c55e' : '#ef4444',
                                                    minWidth: '90px',
                                                    textAlign: 'right',
                                                    fontSize: '0.9375rem'
                                                }}
                                            >
                                                {(strategy.totalPnL || 0) >= 0 ? '+' : ''}${(strategy.totalPnL || 0).toLocaleString()}
                                            </Typography>

                                            {/* View Button */}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleViewDetails(strategy)}
                                                sx={{
                                                    textTransform: 'none',
                                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                                    color: '#ffffff',
                                                    fontSize: '0.8125rem',
                                                    padding: '4px 12px',
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                        borderColor: '#7c3aed',
                                                        background: 'rgba(124, 58, 237, 0.1)'
                                                    }
                                                }}
                                            >
                                                View
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}

                        {/* Pagination */}
                        {filteredStrategies.length > rowsPerPage && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
                                <Pagination
                                    count={Math.ceil(filteredStrategies.length / rowsPerPage)}
                                    page={page}
                                    onChange={(e, value) => setPage(value)}
                                    sx={{
                                        '& .MuiPaginationItem-root': { color: '#ffffff' },
                                        '& .Mui-selected': { background: '#7c3aed !important', color: '#ffffff' }
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        background: 'rgba(41, 43, 47, 1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#ffffff'
                    }
                }}
            >
                <MenuItem onClick={handleViewDetails}>
                    <VisibilityIcon fontSize="small" sx={{ marginRight: 1 }} />
                    View Details
                </MenuItem>
                {/* B2: Edit MenuItem */}
                <MenuItem onClick={handleEditStrategy}>
                    <EditIcon fontSize="small" sx={{ marginRight: 1, color: '#667eea' }} />
                    Edit Strategy
                </MenuItem>
                {/* C1: Enable/Disable MenuItem */}
                <MenuItem onClick={() => handleToggleEnabled()}>
                    <BlockIcon
                        fontSize="small"
                        sx={{
                            marginRight: 1,
                            color: selectedStrategy?.isEnabled !== false ? '#f59e0b' : '#22c55e'
                        }}
                    />
                    {selectedStrategy?.isEnabled !== false ? 'Disable Strategy' : 'Enable Strategy'}
                </MenuItem>
                <MenuItem onClick={handleForceStop}>
                    <StopIcon fontSize="small" sx={{ marginRight: 1 }} />
                    Force Stop
                </MenuItem>
            </Menu>

            {/* Force Stop Dialog */}
            <Dialog
                open={forceStopDialogOpen}
                onClose={() => setForceStopDialogOpen(false)}
                PaperProps={{
                    sx: {
                        background: 'rgba(41, 43, 47, 1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#ffffff'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon sx={{ color: '#f5576c' }} />
                        <Typography variant="h6">Force Stop Strategy</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ marginBottom: 2 }}>
                        This will immediately stop the strategy, bypassing normal validation checks.
                    </Alert>
                    <Typography variant="body2" sx={{ marginBottom: 2 }}>
                        Strategy: <strong>{strategyToStop?.name}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: 2 }}>
                        User: <strong>{strategyToStop?.userName}</strong>
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason (Optional)"
                        value={stopReason}
                        onChange={(e) => setStopReason(e.target.value)}
                        placeholder="Enter reason for force stopping..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button
                        onClick={() => setForceStopDialogOpen(false)}
                        sx={{ color: '#ffffff' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmForceStop}
                        sx={{
                            background: 'linear-gradient(135deg, #f5576c 0%, #fa709a 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #fa709a 0%, #f5576c 100%)' }
                        }}
                    >
                        Force Stop
                    </Button>
                </DialogActions>
            </Dialog>

            {/* C3: Stop & Disable Confirmation Dialog */}
            <Dialog
                open={stopDisableDialogOpen}
                onClose={() => {
                    setStopDisableDialogOpen(false);
                    setStrategyToDisable(null);
                }}
                PaperProps={{
                    sx: {
                        background: 'rgba(41, 43, 47, 1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#ffffff'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon sx={{ color: '#f59e0b' }} />
                        <Typography variant="h6">Disable Running Strategy?</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ marginBottom: 2 }}>
                        Strategy "{strategyToDisable?.name}" is currently <strong>running</strong>.
                    </Alert>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        The strategy has {strategyToDisable?.openPositions || 0} open position(s).
                        Disabling will:
                    </Typography>
                    <Box component="ul" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1, mb: 2 }}>
                        <li>Stop the strategy immediately</li>
                        <li>Prevent users from starting it</li>
                        <li>Keep existing positions open</li>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#f59e0b' }}>
                        Are you sure you want to stop and disable this strategy?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button
                        onClick={() => {
                            setStopDisableDialogOpen(false);
                            setStrategyToDisable(null);
                        }}
                        sx={{ color: '#ffffff' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmStopAndDisable}
                        sx={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' }
                        }}
                    >
                        Stop & Disable
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Action Dialog */}
            <Dialog
                open={bulkActionDialogOpen}
                onClose={() => setBulkActionDialogOpen(false)}
                PaperProps={{
                    sx: {
                        background: 'rgba(41, 43, 47, 1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#ffffff'
                    }
                }}
            >
                <DialogTitle>Bulk Action</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ marginBottom: 2 }}>
                        Selected {selectedStrategyIds.length} strateg{selectedStrategyIds.length === 1 ? 'y' : 'ies'}
                    </Typography>
                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <InputLabel sx={{ color: '#ffffff' }}>Action</InputLabel>
                        <Select
                            value={bulkAction}
                            label="Action"
                            onChange={(e) => setBulkAction(e.target.value)}
                            sx={{
                                color: '#ffffff',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' },
                                '.MuiSvgIcon-root': { color: '#ffffff' }
                            }}
                        >
                            <MenuItem value="stop">Stop All</MenuItem>
                            <MenuItem value="start">Start All</MenuItem>
                        </Select>
                    </FormControl>
                    <Alert severity="info">
                        This action will {bulkAction} all selected strategies.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button
                        onClick={() => setBulkActionDialogOpen(false)}
                        sx={{ color: '#ffffff' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmBulkAction}
                        sx={{
                            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' }
                        }}
                    >
                        Execute Bulk Action
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modals */}
            <StrategyDetailsModal
                open={detailsModalOpen}
                onClose={() => setDetailsModalOpen(false)}
                strategy={selectedStrategyForDetails}
                onRefresh={fetchAllData}
            />

            <CreateStrategyWizard
                open={createWizardOpen}
                onClose={() => {
                    setCreateWizardOpen(false);
                }}
                onSave={() => {
                    setCreateWizardOpen(false);
                    fetchAllData();
                }}
            />

            {/* B2: Edit Strategy Modal */}
            <EditStrategyModal
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setSelectedStrategyForEdit(null);
                }}
                strategy={selectedStrategyForEdit}
                onSave={handleEditSave}
            />
        </Box>
    );
});

AdminStrategyManagement.displayName = 'AdminStrategyManagement';

export default AdminStrategyManagement;
